import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { api } from 'common';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import AlertDialog from '../components/AlertDialog';
import CircularLoading from '../components/CircularLoading';
import qrStickerTemplate from '../assets/img/qr_sticker_template.png';
import packagingTemplate from '../assets/img/packaging_template.png';

const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });
};

// --- Packaging box artwork geometry --------------------------------------------------
// Measured directly from the source artwork ("Packaging New_Final 1.pdf", one page,
// 729.34pt x 563.44pt) via its vector shape/text coordinates, then converted to mm
// (1pt = 25.4/72mm) to match jsPDF's `unit: 'mm'`. If the artwork is ever redesigned,
// re-measure these against the new file's own geometry rather than eyeballing a
// screenshot - a few mm off here prints crooked on real boxes.
const PT_TO_MM = 25.4 / 72;
const PACKAGING_PAGE_WIDTH_MM = 729.3369750976562 * PT_TO_MM;  // ~257.29mm
const PACKAGING_PAGE_HEIGHT_MM = 563.4359741210938 * PT_TO_MM; // ~198.77mm

// Gold-framed square on the back panel where "SCAN TO DOWNLOAD THE APP" QR sits.
const PACKAGING_QR_BOX = {
    x: 475.9 * PT_TO_MM,
    y: 235.6 * PT_TO_MM,
    size: 45 * PT_TO_MM
};

// End of the "Batch No.:" label on the back panel footer, and roughly its text baseline
// (~1pt above the label's bbox bottom, to compensate for descender space in the bbox).
// maxWidth is how far the value can run before hitting the back panel's own right edge
// (611.8pt) with a small margin - the printed value is a free-text batchNumber plus a
// sequence suffix, so its length isn't bounded; see drawFittedText below for how it's kept
// inside this box regardless of how long that text turns out to be.
const PACKAGING_BATCH_LABEL = {
    afterX: 541.92 * PT_TO_MM,
    baselineY: 441.0 * PT_TO_MM,
    maxWidth: (605.0 - 541.92) * PT_TO_MM
};

// The "PRICE / QAR 50/YEAR | QATAR EDITION" gold pill banner on the back panel originally
// ran the full row (pt 435.20-561.63 x 376.17-403.26). packaging_template.png has since
// been edited to shrink that whole pill to 65% of its width, anchored at its original
// top-left corner, freeing this box for the barcode in the same row - re-measure and
// re-shrink from the source PDF's own geometry if the artwork changes again.
const PACKAGING_BARCODE_BOX = {
    x: 517.38 * PT_TO_MM,
    y: 376.17 * PT_TO_MM,
    width: (605.0 - 517.38) * PT_TO_MM,
    height: (403.28 - 376.17) * PT_TO_MM
};

// Fixed product barcode: a randomly-picked 12-digit number with its EAN-13 check digit
// appended, generated once and printed identically on every package (not per-serial or
// per-batch) - per explicit instruction, this does not vary between print runs.
const PACKAGE_BARCODE_NUMBER = '5614225910348';

// --- QR sticker geometry --------------------------------------------------------------
// The shield-shaped sticker artwork ("Packaging New_Final 2.pdf") was extracted (raster +
// its soft mask, composited to a transparent PNG, then cropped tight to the shield's own
// silhouette - no padding baked in) at its native aspect ratio, ~39.69mm x 68.85mm. The
// shield is narrower than the required 65mm x 85mm sticker footprint, so it's scaled up
// uniformly (preserving its shape, no stretching) to be as large as it can be inside that
// 65x85mm cell - filling the full 85mm height, centered with blank margin left/right - so
// the physical sticker slot stays exactly the same size as the old rectangular card while
// the artwork itself reads at full size rather than shrunken inside its own canvas.
// The QR box position was located by pixel-detecting the gold border rectangle baked into
// the artwork, then expressed as fractions of the shield's own width/height so it tracks
// correctly regardless of scale - re-detect against the new file's own pixels if the art
// changes again rather than eyeballing a screenshot.
const STICKER_QR_BOX_FRACTION = { x: 0.2575, y: 0.4085, w: 0.4796, h: 0.2765 };

// Scales (w,h) down to fit inside (maxW,maxH) without distorting the aspect ratio.
const fitContain = (w, h, maxW, maxH) => {
    const scale = Math.min(maxW / w, maxH / h);
    return { width: w * scale, height: h * scale };
};

// Draws `text` at (x,y) using `font`, shrinking the font size (from `maxSize` down to
// `minSize`) until it fits within `maxWidth` (doc units, i.e. mm here) - for text whose
// length isn't bounded (free-text admin input plus a sequence suffix) going into a fixed
// print box, rather than betting a single guessed font size will always fit.
const drawFittedText = (doc, text, x, y, maxWidth, { font = 'helvetica', style = 'normal', maxSize = 6, minSize = 3.5 } = {}) => {
    doc.setFont(font, style);
    let size = maxSize;
    doc.setFontSize(size);
    while (size > minSize && doc.getTextWidth(text) > maxWidth) {
        size -= 0.25;
        doc.setFontSize(size);
    }
    doc.text(text, x, y);
};

// Renders `value` as a standard EAN-13 barcode (with the human-readable digits
// underneath) onto a canvas at the library's own natural size - callers fit/center it
// into whatever box they have via `fitContain`.
const generateBarcodeCanvas = (value) => {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, value, {
        format: 'EAN13',
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 16,
        textMargin: 4,
        margin: 6,
        background: '#ffffff',
        lineColor: '#000000'
    });
    return canvas;
};


const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: 'var(--bg-main)',
        minHeight: '100vh',
        padding: '24px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
    },
    card: {
        borderRadius: '20px',
        backgroundColor: 'var(--bg-card)',
        border: 'var(--card-border)',
        boxShadow: 'var(--shadow)',
        width: '100%',
        maxWidth: '800px',
        padding: '30px',
        marginTop: '20px'
    },
    textField: {
        marginBottom: '20px',
        "& label.Mui-focused": {
            color: '#E9AE31',
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: '#E9AE31',
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: '#E9AE31',
            },
            "& input": {
                color: 'var(--text-main)'
            }
        },
        "& .MuiInputLabel-root": {
            color: 'var(--text-secondary)'
        }
    },
    btn: {
        borderRadius: '19px',
        backgroundColor: '#E9AE31',
        minHeight: '50px',
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '16px',
        boxShadow: '0 4px 12px rgba(233, 174, 49, 0.2)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: '#D19825',
            boxShadow: '0 6px 16px rgba(233, 174, 49, 0.3)',
        }
    }
}));

export default function GenerateQr() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const classes = useStyles();
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settingsdata.settings);

    const { generateUniqueSerialNumbers, getSerialSeries, addSerialSeries, SERIAL_PREFIXES, PAYMENT_EXEMPT_PREFIXES } = api;

    // Seeded with the 3 legacy defaults so the dropdown isn't blank while getSerialSeries()
    // loads the real (admin-configurable) list on mount.
    const [seriesList, setSeriesList] = useState(
        SERIAL_PREFIXES.map((prefix) => ({ prefix, prePaid: PAYMENT_EXEMPT_PREFIXES.includes(prefix) }))
    );
    const [series, setSeries] = useState(SERIAL_PREFIXES[0]);
    const [rangeCount, setRangeCount] = useState('10');
    const [batchNumber, setBatchNumber] = useState('');
    const [baseUrl, setBaseUrl] = useState('https://rahl.me');
    const [includePackaging, setIncludePackaging] = useState(false);
    const [startSequence, setStartSequence] = useState('1');
    const [loading, setLoading] = useState(false);
    const [alertState, setAlertState] = useState({ open: false, msg: '' });

    // Custom-series admin form ("add the option to add a custom batch like SNO/AMZ, with a
    // pre-paid toggle") - separate loading/state from the main generate flow below.
    const [newSeriesPrefix, setNewSeriesPrefix] = useState('');
    const [newSeriesPrePaid, setNewSeriesPrePaid] = useState(false);
    const [addingSeries, setAddingSeries] = useState(false);

    const loadSeriesList = async () => {
        const res = await dispatch(getSerialSeries());
        if (res && res.success && Array.isArray(res.series) && res.series.length > 0) {
            setSeriesList(res.series);
            // Keep the current selection if it's still valid, else default to the first entry.
            setSeries((prev) => (res.series.some((s) => s.prefix === prev) ? prev : res.series[0].prefix));
        }
    };

    useEffect(() => {
        loadSeriesList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAddSeries = async () => {
        const prefix = newSeriesPrefix.trim().toUpperCase();
        if (!/^[A-Z0-9]{2,6}$/.test(prefix)) {
            setAlertState({ open: true, msg: t('invalid_series_prefix') });
            return;
        }
        if (settings && settings.AllowCriticalEditsAdmin === false) {
            setAlertState({ open: true, msg: t('demo_mode') });
            return;
        }

        setAddingSeries(true);
        try {
            const res = await dispatch(addSerialSeries(prefix, newSeriesPrePaid));
            if (!res || !res.success) {
                throw new Error((res && res.error) || 'Failed to add series');
            }
            await loadSeriesList();
            setSeries(prefix);
            setNewSeriesPrefix('');
            setNewSeriesPrePaid(false);
            setAlertState({ open: true, msg: `${t('series_added_success')} "${prefix}"` });
        } catch (error) {
            setAlertState({ open: true, msg: error.message || 'Failed to add series' });
        } finally {
            setAddingSeries(false);
        }
    };

    const handleAlertClose = (e) => {
        e.preventDefault();
        setAlertState({ open: false, msg: '' });
    };

    const handleGenerate = async () => {
        if (!seriesList.some((s) => s.prefix === series)) {
            setAlertState({ open: true, msg: t('invalid_series') });
            return;
        }

        const count = parseInt(rangeCount, 10);
        if (isNaN(count) || count <= 0 || count > 500) {
            setAlertState({ open: true, msg: t('invalid_range') });
            return;
        }

        const trimmedBatch = batchNumber.trim();
        if (!trimmedBatch) {
            setAlertState({ open: true, msg: t('invalid_batch_number') });
            return;
        }

        // Used for the per-unit "<batchNumber>-<seq>" label printed under each sticker pair
        // and (if included) on the packaging box - not just a packaging-only concept anymore.
        const startSeq = parseInt(startSequence, 10);
        if (isNaN(startSeq) || startSeq < 1) {
            setAlertState({ open: true, msg: t('invalid_start_sequence') });
            return;
        }

        setLoading(true);

        try {
            // Check if Admin allowed edits
            if (settings && settings.AllowCriticalEditsAdmin === false) {
                setAlertState({ open: true, msg: t('demo_mode') });
                setLoading(false);
                return;
            }

            // 1. Generate unique random alpha-numeric serial numbers and persist them
            const genRes = await dispatch(generateUniqueSerialNumbers(series, count, trimmedBatch, startSeq));
            if (!genRes || !genRes.success) {
                throw new Error(genRes?.error || 'Failed to generate serial numbers');
            }
            const serials = genRes.serials;

            // Shared per-unit label numbering ("<batchNumber>-<seq>") - one number per unique
            // serial, used both under each sticker pair below and (if included) on the
            // packaging box's "Batch No." field, so a sticker pair and its matching box carry
            // the exact same label for easy matching during packing/QC.
            const unitSeqDigits = Math.max(3, String(startSeq + serials.length - 1).length);
            const unitLabel = (unitIndex) => `${trimmedBatch}-${String(startSeq + unitIndex).padStart(unitSeqDigits, '0')}`;

            // 2. Generate QR Codes and construct PDF
            const imgElement = await loadImage(qrStickerTemplate);
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // Grid Layout Settings (A4 landscape is 297mm x 210mm)
            // Sticker size stays 6.5cm x 8.5cm = 65mm x 85mm - unchanged from the old
            // rectangular card design, only the artwork inside that footprint changed.
            const cardWidth = 65;
            const cardHeight = 85;
            const cols = 4;
            const rows = 2;
            const cardsPerPage = cols * rows; // 8 stickers per A4 landscape page
            const colGap = 3;
            const rowGap = 8;
            // Margins solved so cols/rows fill the page exactly:
            // marginLeft*2 + cardWidth*cols + colGap*(cols-1) = 297
            // marginTop*2 + cardHeight*rows + rowGap*(rows-1) = 210
            const pageWidth = 297;
            const pageHeight = 210;
            const marginLeft = (pageWidth - (cardWidth * cols + colGap * (cols - 1))) / 2;
            const marginTop = (pageHeight - (cardHeight * rows + rowGap * (rows - 1))) / 2;

            // The shield artwork is narrower than the 65x85mm cell - scale it up as large as
            // it can go without distorting its shape (fills the full 85mm height, centered
            // with blank margin left/right), same for every sticker so this is computed once.
            const stickerFit = fitContain(imgElement.width, imgElement.height, cardWidth, cardHeight);
            const stickerOffsetX = (cardWidth - stickerFit.width) / 2;
            const stickerOffsetY = (cardHeight - stickerFit.height) / 2;

            // Normalize Base URL
            let base = baseUrl.trim();
            if (!base.endsWith('/')) {
                base += '/';
            }

            // Each serial is printed twice, side by side, so its pair is never split across
            // a row or page break. `cols` is a multiple of the pair size (2) precisely so
            // that every row boundary and every page boundary (cardsPerPage is a multiple of
            // cols) always lands on a pair boundary too - a row can only ever hold whole
            // pairs, never a fragment of a third serial.
            const printSerials = serials.flatMap((serial) => [serial, serial]);

            // Sequence label width matches the total printed sticker count (including
            // duplicates) so e.g. a 100-card sheet reads "001-008 of 100" rather than "1-8".
            const seqDigits = String(printSerials.length).length;
            const pageBottomY = pageHeight - marginTop / 2;

            // One line per page, centered in the blank margin below the last row of cards -
            // not printed on/under each individual sticker. `startIdx`/`endIdx` are the
            // 0-indexed range of printed stickers on that page, in the same top-left-to-
            // bottom-right order the grid below lays them out in.
            const drawPageFooter = (startIdx, endIdx) => {
                const label = `${trimmedBatch} - Cards ${String(startIdx + 1).padStart(seqDigits, '0')}-${String(endIdx + 1).padStart(seqDigits, '0')} of ${printSerials.length}`;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(120, 120, 120);
                doc.text(label, pageWidth / 2, pageBottomY, { align: 'center' });
            };

            let pageStartIndex = 0;
            // Cache one QR data URL per unique serial (rather than per printed sticker) so
            // the duplicate of each pair doesn't re-run QR generation for the same content.
            const qrDataUrlBySerial = {};

            for (let k = 0; k < printSerials.length; k++) {
                const serial = printSerials[k];

                // Add new page if we transition to the next set of stickers
                if (k > 0 && k % cardsPerPage === 0) {
                    drawPageFooter(pageStartIndex, k - 1);
                    doc.addPage();
                    pageStartIndex = k;
                }

                // Position inside grid
                const cellIndex = k % cardsPerPage;
                const row = Math.floor(cellIndex / cols);
                const col = cellIndex % cols;

                const x = marginLeft + col * (cardWidth + colGap);
                const y = marginTop + row * (cardHeight + rowGap);

                // Draw the shield artwork centered in the cell at its contain-fit size (see
                // `stickerFit` above) rather than stretched to fill the whole 65x85mm cell.
                doc.addImage(
                    imgElement, 'PNG',
                    x + stickerOffsetX, y + stickerOffsetY,
                    stickerFit.width, stickerFit.height
                );

                if (!qrDataUrlBySerial[serial]) {
                    // Construct Scan Link (Obfuscated base64 serial)
                    const scanUrl = `${base}?qr=${window.btoa(serial)}`;

                    // Generate QR code data URL. Error correction is bumped to 'H' since the
                    // serial number is no longer printed as a human-readable fallback below it.
                    qrDataUrlBySerial[serial] = await QRCode.toDataURL(scanUrl, {
                        margin: 1,
                        width: 360,
                        errorCorrectionLevel: 'H',
                        color: {
                            dark: '#ffffff', // Brand gold color
                            light: '#121212' // Brand black color matching the sticker's QR box
                        }
                    });
                }

                // Add QR Image inside the gold-bordered box on the shield artwork - position
                // is STICKER_QR_BOX_FRACTION applied against the shield's own fitted size,
                // not the full cell, since the shield doesn't fill the cell edge-to-edge.
                doc.addImage(
                    qrDataUrlBySerial[serial], 'PNG',
                    x + stickerOffsetX + STICKER_QR_BOX_FRACTION.x * stickerFit.width,
                    y + stickerOffsetY + STICKER_QR_BOX_FRACTION.y * stickerFit.height,
                    STICKER_QR_BOX_FRACTION.w * stickerFit.width,
                    STICKER_QR_BOX_FRACTION.h * stickerFit.height
                );

                // Print this pair's "<batchNumber>-<seq>" label once, centered underneath,
                // right after placing the second sticker of that pair (cols 1 and 3 in this
                // 4-col grid) - one caption shared between the two identical stickers rather
                // than repeating it under each one individually. Each pair gets its own
                // incrementing seq (not the same static batch text repeated) so it matches
                // the label on that same unit's packaging box one-to-one.
                if (col % 2 === 1) {
                    const pairIndex = Math.floor(k / 2);
                    const pairLeftX = x - (cardWidth + colGap);
                    const pairCenterX = pairLeftX + (cardWidth * 2 + colGap) / 2;
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(7);
                    doc.setTextColor(120, 120, 120);
                    doc.text(unitLabel(pairIndex), pairCenterX, y + cardHeight + 4, { align: 'center' });
                }
            }

            // Footer for the last (possibly partial) page.
            drawPageFooter(pageStartIndex, printSerials.length - 1);

            // Save PDF
            doc.save(`RAHL_QR_Codes_${series}_${count}_${Date.now()}.pdf`);

            // 3. Optionally also generate the packaging box artwork - one full box net per
            // PDF page (not a grid, unlike the stickers above), each stamped with the same
            // per-unit QR, the fixed product barcode, and the same "<batchNumber>-<seq>"
            // label as that unit's sticker pair (see unitLabel above).
            if (includePackaging) {
                const pkgImg = await loadImage(packagingTemplate);

                // Same barcode image on every page - the number is fixed, not per-unit - so
                // it's generated once up front instead of once per package.
                const barcodeCanvas = generateBarcodeCanvas(PACKAGE_BARCODE_NUMBER);
                const barcodeFit = fitContain(
                    barcodeCanvas.width, barcodeCanvas.height,
                    PACKAGING_BARCODE_BOX.width, PACKAGING_BARCODE_BOX.height
                );
                const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');
                const barcodeX = PACKAGING_BARCODE_BOX.x + (PACKAGING_BARCODE_BOX.width - barcodeFit.width) / 2;
                const barcodeY = PACKAGING_BARCODE_BOX.y + (PACKAGING_BARCODE_BOX.height - barcodeFit.height) / 2;

                const pkgDoc = new jsPDF({
                    orientation: 'landscape',
                    unit: 'mm',
                    format: [PACKAGING_PAGE_WIDTH_MM, PACKAGING_PAGE_HEIGHT_MM]
                });

                for (let k = 0; k < serials.length; k++) {
                    const serial = serials[k];

                    if (k > 0) {
                        pkgDoc.addPage([PACKAGING_PAGE_WIDTH_MM, PACKAGING_PAGE_HEIGHT_MM], 'landscape');
                    }

                    pkgDoc.addImage(pkgImg, 'PNG', 0, 0, PACKAGING_PAGE_WIDTH_MM, PACKAGING_PAGE_HEIGHT_MM);

                    // Same scan link the sticker's QR carries, so scanning either does the
                    // exact same thing. Standard (dark-on-light) colors here, unlike the
                    // sticker's inverted card-template palette, since this QR sits in a
                    // light square already drawn into the box artwork.
                    const scanUrl = `${base}?qr=${window.btoa(serial)}`;
                    const pkgQrDataUrl = await QRCode.toDataURL(scanUrl, {
                        margin: 0,
                        width: 300,
                        errorCorrectionLevel: 'H',
                        color: { dark: '#000000', light: '#ffffff' }
                    });
                    pkgDoc.addImage(
                        pkgQrDataUrl, 'PNG',
                        PACKAGING_QR_BOX.x, PACKAGING_QR_BOX.y,
                        PACKAGING_QR_BOX.size, PACKAGING_QR_BOX.size
                    );

                    // Same "<batchNumber>-<seq>" label as this unit's sticker pair caption -
                    // deliberately not a separately-invented code, so a box and its matching
                    // sticker pair are trivially identifiable as belonging together.
                    const boxLabel = unitLabel(k);

                    // Fixed product barcode (same on every package) in the space freed up
                    // next to the shrunk PRICE/QATAR EDITION banner - see PACKAGING_BARCODE_BOX.
                    pkgDoc.addImage(barcodeDataUrl, 'PNG', barcodeX, barcodeY, barcodeFit.width, barcodeFit.height);

                    // Printed right after the artwork's own "Batch No.:" text - shrinks to
                    // fit since boxLabel's length depends on the admin's free-text batchNumber.
                    pkgDoc.setTextColor(255, 255, 255);
                    drawFittedText(
                        pkgDoc, boxLabel,
                        PACKAGING_BATCH_LABEL.afterX + 1, PACKAGING_BATCH_LABEL.baselineY,
                        PACKAGING_BATCH_LABEL.maxWidth - 1
                    );
                }

                pkgDoc.save(`RAHL_Packaging_${series}_${count}_${Date.now()}.pdf`);
            }

            setAlertState({
                open: true,
                msg: includePackaging
                    ? `Successfully generated and saved ${count} serial numbers! Downloading sticker and packaging PDFs...`
                    : `Successfully generated and saved ${count} serial numbers! Downloading PDF...`
            });

        } catch (error) {
            console.error('Error generating QR Codes:', error);
            setAlertState({ open: true, msg: error.message || 'An error occurred during QR code generation.' });
        } finally {
            setLoading(false);
        }
    };

    return loading ? (
        <CircularLoading />
    ) : (
        <div className={classes.container}>
            <Card className={classes.card}>
                <CardContent style={{ padding: '10px' }}>
                    <Typography
                        variant="h5"
                        style={{
                            marginBottom: '25px',
                            textAlign: isRTL === 'rtl' ? 'right' : 'left',
                            fontWeight: 'bold',
                            fontFamily: 'Inter, sans-serif',
                            color: 'var(--text-main)'
                        }}
                    >
                        {t('generate_qr')}
                    </Typography>

                    <Grid container spacing={3} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth className={classes.textField}>
                                <InputLabel id="seriesLabel">{t('serial_series')}</InputLabel>
                                <Select
                                    labelId="seriesLabel"
                                    id="series"
                                    value={series}
                                    label={t('serial_series')}
                                    onChange={(e) => setSeries(e.target.value)}
                                >
                                    {seriesList.map(({ prefix, prePaid }) => (
                                        <MenuItem key={prefix} value={prefix}>
                                            {prefix}{prePaid ? ` (${t('pre_paid_label')})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={t('serial_count')}
                                id="rangeCount"
                                value={rangeCount}
                                fullWidth
                                onChange={(e) => setRangeCount(e.target.value)}
                                className={classes.textField}
                                placeholder="e.g. 10"
                                type="number"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('batch_number')}
                                id="batchNumber"
                                value={batchNumber}
                                fullWidth
                                onChange={(e) => setBatchNumber(e.target.value)}
                                className={classes.textField}
                                placeholder="e.g. BATCH001"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('starting_batch_sequence')}
                                id="startSequence"
                                value={startSequence}
                                fullWidth
                                onChange={(e) => setStartSequence(e.target.value)}
                                className={classes.textField}
                                placeholder="e.g. 1"
                                type="number"
                                helperText={t('starting_batch_sequence_help')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('verification_base_url')}
                                id="baseUrl"
                                value={baseUrl}
                                fullWidth
                                onChange={(e) => setBaseUrl(e.target.value)}
                                className={classes.textField}
                                placeholder="e.g. https://rahl.me"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <div
                                style={{
                                    border: '1px dashed rgba(233, 174, 49, 0.4)',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginBottom: '4px'
                                }}
                            >
                                <Typography style={{ color: 'var(--text-main)', fontWeight: 'bold', marginBottom: '12px' }}>
                                    {t('add_custom_series')}
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label={t('series_prefix')}
                                            value={newSeriesPrefix}
                                            fullWidth
                                            onChange={(e) => setNewSeriesPrefix(e.target.value.toUpperCase())}
                                            className={classes.textField}
                                            placeholder="e.g. XYZ"
                                            inputProps={{ maxLength: 6 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={newSeriesPrePaid}
                                                    onChange={(e) => setNewSeriesPrePaid(e.target.checked)}
                                                    style={{ color: '#E9AE31' }}
                                                />
                                            }
                                            label={<Typography style={{ color: 'var(--text-main)' }}>{t('pre_paid_label')}</Typography>}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            onClick={handleAddSeries}
                                            disabled={addingSeries}
                                            variant="outlined"
                                            fullWidth
                                            style={{ borderColor: '#E9AE31', color: '#E9AE31', minHeight: '40px' }}
                                        >
                                            {addingSeries ? t('adding') : t('add_series_btn')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includePackaging}
                                        onChange={(e) => setIncludePackaging(e.target.checked)}
                                        style={{ color: '#E9AE31' }}
                                    />
                                }
                                label={<Typography style={{ color: 'var(--text-main)' }}>{t('include_packaging')}</Typography>}
                            />
                        </Grid>

                        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                            <Button
                                className={classes.btn}
                                onClick={handleGenerate}
                                variant="contained"
                                fullWidth
                                style={{ maxWidth: '400px' }}
                            >
                                <Typography style={{ fontWeight: 'bold', color: '#fff' }}>
                                    {t('generate_btn')}
                                </Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <AlertDialog open={alertState.open} onClose={handleAlertClose}>
                {alertState.msg}
            </AlertDialog>
        </div>
    );
}
